/**
 * Display formatting for wellness values.
 *
 * THE ONE TRAP IN THIS MODULE: weight is STORED IN INTEGER GRAMS but its metric type
 * declares `unit: 'lb'`, because grams is the storage unit and pounds is what Brian reads.
 * Every other metric stores in the unit it displays. So a component that renders
 * `${value.v} ${metric.unit}` is correct for blood pressure, waist, resting HR and VO2 max
 * — and silently prints "96796 lb" for weight.
 *
 * `formatReading()` is therefore the ONLY sanctioned way to render a reading. Nothing in
 * the module should interpolate a raw value next to a unit.
 */
import type { WellnessMetricType, WellnessReadingValue } from '../../shared/types';

const GRAMS_PER_LB = 453.59237;

export const lbToGrams = (lb: number): number => Math.round(lb * GRAMS_PER_LB);
export const gramsToLb = (g: number): number => g / GRAMS_PER_LB;

/** Metrics whose stored unit is not their display unit. Weight is currently the only one. */
const STORED_IN_GRAMS = new Set(['weight']);

export function isStoredInGrams(slug: string): boolean {
  return STORED_IN_GRAMS.has(slug);
}

/** "134/88" for a pressure, "213.4 lb" for a weight, "62 bpm" for a scalar. */
export function formatReading(
  value: WellnessReadingValue,
  metric: Pick<WellnessMetricType, 'slug' | 'unit'>
): string {
  if ('systolic' in value) return `${value.systolic}/${value.diastolic}`;
  if (isStoredInGrams(metric.slug)) return `${gramsToLb(value.v).toFixed(1)} ${metric.unit}`;
  return `${trimNumber(value.v)} ${metric.unit}`;
}

/** The number alone, in display units — for chart axes and averages. */
export function displayNumber(value: WellnessReadingValue, slug: string): number {
  if ('systolic' in value) return value.systolic;
  return isStoredInGrams(slug) ? gramsToLb(value.v) : value.v;
}

/** Turn a typed display value into what the API stores. */
export function toStored(input: number, slug: string): number {
  return isStoredInGrams(slug) ? lbToGrams(input) : input;
}

function trimNumber(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

/**
 * A macro total for display. `null` renders as an em dash, NEVER as "0" — a zero reads as
 * a measured value and stops anyone from noticing the gap. This is the display half of the
 * rule the schema and the router already enforce.
 */
export function macro(value: number | null, unit = ''): string {
  if (value === null) return '—';
  return unit ? `${Math.round(value)} ${unit}` : String(Math.round(value));
}

/**
 * `macro()` with thousands separators, for the places a macro total sits next to its target
 * ("2,760 mg of ≤ 2,300"). Same null rule, deliberately delegated rather than reimplemented:
 * a second null-handling branch is a second place for a 0 to leak in. `macro()` itself is
 * left alone because other screens render it inside tighter cells.
 */
export function macroFormatted(value: number | null): string {
  if (value === null) return macro(null);
  return Math.round(value).toLocaleString('en-US');
}

const MACRO_LABELS: Record<string, string> = {
  calories: 'Calories',
  protein_g: 'Protein',
  fat_g: 'Fat',
  carb_g: 'Carbs',
  sodium_mg: 'Sodium',
  potassium_mg: 'Potassium',
};

export const macroLabel = (key: string): string => MACRO_LABELS[key] ?? key;

/** The six macro totals, in the order they read best, with the unit each is stored in. */
export const MACRO_KEYS = [
  'calories',
  'protein_g',
  'carb_g',
  'fat_g',
  'sodium_mg',
  'potassium_mg',
] as const;

export type MacroKey = (typeof MACRO_KEYS)[number];

const MACRO_UNITS: Record<MacroKey, string> = {
  calories: 'kcal',
  protein_g: 'g',
  carb_g: 'g',
  fat_g: 'g',
  sodium_mg: 'mg',
  potassium_mg: 'mg',
};

export const macroUnit = (key: MacroKey): string => MACRO_UNITS[key];

/**
 * Which way a target runs: a floor to reach, or a ceiling to stay under.
 *
 * READ FROM THE ROW. This was a mirrored copy of a hardcoded key list in the trends
 * endpoint — one fact in two files, about a value Brian edits, so any target he added
 * rendered as a ceiling whether it was one or not. `wellness_targets.direction` now carries
 * it (migration 0028) and both sides read the same column.
 *
 * The slug fallback exists only for a row written before the column did.
 */
const LEGACY_FLOORS = new Set(['protein_g', 'potassium_mg', 'water_oz']);

export const targetDirection = (
  slug: string,
  target?: { direction?: 'up' | 'down' | null }
): 'up' | 'down' => target?.direction ?? (LEGACY_FLOORS.has(slug) ? 'up' : 'down');

/**
 * A target's numeric value, or null when it isn't a number at all — `wellness_targets.value`
 * is TEXT because the same column holds '2300' and '12:00', and `Number('12:00')` is NaN,
 * which would silently plot an eating-window bound as a macro ceiling.
 */
export function targetNumber(value: string): number | null {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export const SESSION_LABELS: Record<string, string> = {
  strength: 'Strength',
  zone2: 'Zone 2',
  interval: 'Intervals',
  isometric: 'Isometric',
  spine: 'Spine work',
  run: 'Run',
  other: 'Other',
};

/**
 * "Aug 16" for an INSTANT (an ISO datetime like `recorded_at` or `eaten_at`).
 *
 * Do NOT pass a bare YYYY-MM-DD to this — use `shortDay`. `new Date('2026-08-17')` parses
 * as UTC midnight and then renders in local time, which in US Eastern is 8pm the PREVIOUS
 * day. A phase starting 2026-08-17 displayed as "Aug 16", and a doctor's appointment on the
 * 29th displayed as the 28th. Same class of mistake as the UTC day-boundary bug on the
 * server: a calendar date is not an instant.
 */
export function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * "Aug 17" for a CALENDAR DATE (a bare YYYY-MM-DD like `starts_on`, `due_on`,
 * `session_date`). Parsed as LOCAL midnight so the day it names is the day it shows.
 */
export function shortDay(isoDate: string): string {
  return new Date(`${isoDate}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

export function shortTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}
